import { onScopeDispose, ref, watch, type ComputedRef } from 'vue';
import type vtkImageData from '@kitware/vtk.js/Common/DataModel/ImageData';
import type { PanoramicConfig } from '@/src/io/state-file/schema';
import { detectAutomaticArch } from '@/src/panoramic/automatic';
import { isCompleteArch, sampleArch } from '@/src/panoramic/geometry';

export function useAutomaticPanoramicArch(
  image: ComputedRef<vtkImageData | null | undefined>,
  config: ComputedRef<PanoramicConfig | null>,
  loading: ComputedRef<boolean>
) {
  const detecting = ref(false);
  const notice = ref('');
  let pending: AbortController | null = null;
  function cancel() {
    pending?.abort();
    pending = null;
    detecting.value = false;
    notice.value = '';
  }
  async function generate() {
    cancel();
    const volume = image.value;
    const state = config.value;
    if (!volume || !state || loading.value) return;
    const bounds = volume.getBounds();
    state.axialPosition = (bounds[4] + bounds[5]) / 2;
    const originalPoints = state.points;
    const controller = new AbortController();
    pending = controller;
    detecting.value = true;
    try {
      const result = await detectAutomaticArch(volume, controller.signal);
      if (
        controller.signal.aborted ||
        config.value !== state ||
        image.value !== volume ||
        state.points !== originalPoints
      )
        return;
      if (
        result &&
        isCompleteArch(
          result.points,
          sampleArch(result.points, result.axialPosition),
          volume.getBounds()
        )
      ) {
        state.points = result.points;
        state.axialPosition = result.axialPosition;
        state.position = 0.5;
        state.curveSource = 'automatic';
      } else
        notice.value =
          'No se pudo detectar una arcada completa en el corte central. Puede trazarla manualmente.';
    } catch {
      if (!controller.signal.aborted)
        notice.value =
          'No se pudo generar la curva automática. Puede trazarla manualmente.';
    } finally {
      if (pending === controller) {
        pending = null;
        detecting.value = false;
      }
    }
  }
  watch(
    [image, config, loading],
    () => {
      cancel();
      if (
        image.value &&
        !loading.value &&
        config.value &&
        !config.value.points.length &&
        config.value.curveSource !== 'manual'
      )
        void generate();
    },
    { immediate: true }
  );
  onScopeDispose(cancel);
  return { detecting, notice, generate, cancel };
}
